import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card,Modal, CardContent, Button, FormControl,
  InputLabel, MenuItem, Select, TextField, Chip, Paper, CircularProgress,
  CardActionArea, CardHeader, Avatar, IconButton, Divider, Tooltip,
  Checkbox, FormControlLabel, CardActions, Snackbar, Alert,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  Explore as ExploreIcon,
  Chat as ChatIcon,
  AutoStories as StoriesIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import LibraryBrowser from '../components/LibraryBrowser';
import {useLanguage} from "../context/LanguageContext";
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
// Single source of truth (covers es-419, pt-BR, and any future language).
import { getLanguageOptions, PROFICIENCY_LEVELS } from '../config/languagesConfig';

// Language + proficiency options come from the central config so this page
// never drifts from the rest of the app (it previously omitted es-419 / pt-BR).
const languageOptions = getLanguageOptions({ learningOnly: true });
const proficiencyLevels = PROFICIENCY_LEVELS;

const ContentExplorerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for form inputs - num_ideas defaults to 5
  const [formData, setFormData] = useState({
    target_language: selectedLanguage || '',
    proficiency_level: '',
    content_type: 'both',
    interests: [],
    custom_prompt: '',
    num_ideas: 5  // Fixed to 5
  });

  // Loading state
  const [loading, setLoading] = useState(false);

  // Generated content ideas
  const [contentIdeas, setContentIdeas] = useState([]);

  // Selected idea for starting a session
  const [selectedIdea, setSelectedIdea] = useState(null);

  const [interestsModalOpen, setInterestsModalOpen] = useState(false);

  // Idea library browser ("Browse ideas" modal). Picking an item prefills the
  // prompt box (and content type) so the learner can tweak it before generating.
  const [libraryOpen, setLibraryOpen] = useState(false);

  const handleLibrarySelect = (item) => {
    setFormData(prev => ({
      ...prev,
      custom_prompt: item.body || item.description || item.title,
      content_type: item.content_type || prev.content_type,
    }));
    showNotification('Idea added — tweak it, then generate!', 'success');
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  // Which side of a two-role conversation the learner chose to play ('a' or 'b')
  const [selectedRole, setSelectedRole] = useState('a');

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Initialize form with user preferences
  useEffect(() => {
    if (user?.languages?.length > 0) {
      // Find language in user preferences that matches selected global language
      const selectedUserLanguage = user.languages.find(
        lang => lang.language_code === selectedLanguage
      );

      if (selectedUserLanguage) {
        // Use the matching language and its proficiency level
        setFormData(prev => ({
          ...prev,
          target_language: selectedUserLanguage.language_code,
          proficiency_level: selectedUserLanguage.proficiency_level,
          interests: user.interests?.map(interest => interest.name) || []
        }));
      } else {
        // Fallback to first user language if no match
        const firstLanguage = user.languages[0];
        setFormData(prev => ({
          ...prev,
          target_language: firstLanguage.language_code,
          proficiency_level: firstLanguage.proficiency_level,
          interests: user.interests?.map(interest => interest.name) || []
        }));
      }
    }
  }, [user, selectedLanguage]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle interests selection
  const handleInterestsChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      interests: value
    }));
  };

  // Generate content ideas - always generates 5 ideas
  const handleGenerateIdeas = async () => {
    if (!formData.target_language || !formData.proficiency_level) {
      showNotification('Please select a language and proficiency level', 'error');
      return;
    }

    setLoading(true);
    setContentIdeas([]);
    setSelectedIdea(null);

    try {
      const response = await apiClient.content.generateIdeas({
        target_language: formData.target_language,
        proficiency_level: formData.proficiency_level,
        content_type: formData.content_type,
        interests: formData.interests,
        custom_prompt: formData.custom_prompt,
        num_ideas: 5  // Always 5 ideas
      });

      setContentIdeas(response.data.ideas || []);

      if (response.data.ideas?.length === 0) {
        showNotification('No content ideas could be generated. Try different settings.', 'warning');
      }
    } catch (error) {
      console.error('Error generating content ideas:', error);
      showNotification('Failed to generate content ideas. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Start a learning session with the selected idea
  const handleStartSession = async () => {
    if (!selectedIdea) {
      showNotification('Please select a content idea first', 'error');
      return;
    }

    setLoading(true);

    // Resolve which side the learner plays and which side the AI plays.
    // New scenarios provide a `roles` pair (a/b); fall back to the legacy single `persona`.
    let persona = selectedIdea.persona || null;
    let learnerRole = null;
    if (selectedIdea.roles && selectedIdea.roles.a && selectedIdea.roles.b) {
      const learnerSide = selectedRole === 'b' ? selectedIdea.roles.b : selectedIdea.roles.a;
      const aiSide = selectedRole === 'b' ? selectedIdea.roles.a : selectedIdea.roles.b;
      persona = aiSide;                       // AI plays the side the learner did NOT pick
      learnerRole = learnerSide?.role || null; // short phrase describing the learner's side
    }

    try {
      const response = await apiClient.content.startSession({
        content_id: `content_${contentIdeas.indexOf(selectedIdea)}`,
        target_language: formData.target_language,
        proficiency_level: formData.proficiency_level,
        content_type: selectedIdea.type || 'story',
        content_title: selectedIdea.title,
        content_description: selectedIdea.description,
        persona: persona,
        learner_role: learnerRole
      });

      if (response.data.session_id) {
        // Navigate to the learning session
        navigate(`/session/${response.data.session_id}`);
      } else {
        showNotification('Failed to start session', 'error');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      showNotification('Failed to start session. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Select a content idea
  const handleSelectIdea = (idea) => {
    setSelectedIdea(idea);
    setSelectedRole('a'); // default to the first role until the learner chooses
    setConfirmModalOpen(true);
  };

  // Show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Get language display name
  const getLanguageDisplayName = (code) => {
    const language = languageOptions.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  // Get proficiency level display name
  const getProficiencyDisplayName = (level) => {
    const proficiency = proficiencyLevels.find(p => p.value === level);
    return proficiency ? proficiency.label : level;
  };

  const storiesRef = useRef(null);

    useEffect(() => {
      if (contentIdeas.length > 0 && window.innerWidth < 768) {
        setTimeout(() => {
          storiesRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100); // Slight delay to ensure render
      }
    }, [contentIdeas]);

  // Render available user interests for selection
  const renderUserInterests = () => {
    if (!user?.interests || user.interests.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No interests defined in your profile.
        </Typography>
      );
    }

    // Group interests by category
    const interestsByCategory = {};
    user.interests.forEach(interest => {
      if (!interestsByCategory[interest.category]) {
        interestsByCategory[interest.category] = [];
      }
      interestsByCategory[interest.category].push(interest);
    });



    return (
      <Box>
        {Object.entries(interestsByCategory).map(([category, interests]) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {category}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {interests.map(interest => (
                <Chip
                  key={interest.id}
                  label={interest.name}
                  onClick={() => {
                    const currentInterests = [...formData.interests];
                    if (currentInterests.includes(interest.name)) {
                      setFormData({
                        ...formData,
                        interests: currentInterests.filter(i => i !== interest.name)
                      });
                    } else {
                      setFormData({
                        ...formData,
                        interests: [...currentInterests, interest.name]
                      });
                    }
                  }}
                  color={formData.interests.includes(interest.name) ? "primary" : "default"}
                  variant={formData.interests.includes(interest.name) ? "filled" : "outlined"}
                  clickable
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{pt:'20px',pb: '60px'}}>
    <Box sx={{alignText:'center'}}>
     <Typography variant="h5" gutterBottom>
        Embark on Your Language Adventure
      </Typography>
      <Typography variant="body1" paragraph>
        Explore new worlds through language. Dive into magical cities, intriguing characters, and authentic conversations crafted just for you.
      </Typography>
    </Box>
      <Grid container spacing={3}>
        {/* Content Generator Form */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Set Your Course"
              titleTypographyProps={{ variant: 'h6' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <ExploreIcon />
                </Avatar>
              }
            />

<CardContent>
  <Box component="form" noValidate sx={{ mt: 1, pt:0 }}>
    {/* Topic field with clear label - EXPANDED TO MULTILINE */}
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
      Where will your story take you?
    </Typography>
    <TextField
      fullWidth
      multiline
      rows={4}
      name="custom_prompt"
      value={formData.custom_prompt}
      onChange={handleInputChange}
      placeholder="e.g., Lost in Europe, a job interview, a magical café..."
      margin="dense"
      sx={{
        mb: 1.5,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        }
      }}
    />

    {/* Browse the idea library — for when you don't know what to write */}
    <Button
      variant="outlined"
      fullWidth
      startIcon={<AutoAwesomeIcon />}
      onClick={() => setLibraryOpen(true)}
      sx={{ mb: 3, borderRadius: 2, textTransform: 'none' }}
    >
      Not sure? Browse ideas
    </Button>

    {/* Hidden language selection - kept for API compatibility */}
    <FormControl fullWidth sx={{ mb: 2, display: "none" }}>
      <InputLabel id="language-label">Language</InputLabel>
      <Select
        labelId="language-label"
        id="target_language"
        name="target_language"
        value={formData.target_language}
        onChange={handleInputChange}
        label="Language"
        required
      >
        {languageOptions
          .filter(lang => lang.code !== user?.native_language)
          .map(language => (
            <MenuItem key={language.code} value={language.code}>
              {language.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>

    {/* Content Type replaced with ToggleButtonGroup - mobile friendly */}
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>Story or Conversation?</Typography>
    <ToggleButtonGroup
      value={formData.content_type}
      exclusive
      onChange={(e, newValue) => {
        if (newValue !== null) {
          handleInputChange({
            target: { name: 'content_type', value: newValue }
          });
        }
      }}
      aria-label="content type"
      fullWidth
      size="small"
      sx={{
        mb: 3,
        '& .MuiToggleButton-root': {
          padding: { xs: '8px 0', sm: '8px 16px' },
          fontSize: { xs: '13px', sm: '14px' }
        }
      }}
    >
      <ToggleButton value="story" aria-label="story">
        <StoriesIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        <span></span>
      </ToggleButton>
      <ToggleButton value="conversation" aria-label="conversation">
        <ChatIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        <span></span>
      </ToggleButton>
      <ToggleButton value="both" aria-label="both">
         <StoriesIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        <span>/</span>
        <ChatIcon sx={{ ml:{xs:0.5, sm:1}, mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
      </ToggleButton>
    </ToggleButtonGroup>

    {/* Primary Action Button moved up and mobile optimized */}
    <Box sx={{ mb: 3 }}>
      <Button
        variant="contained"
        fullWidth
        onClick={handleGenerateIdeas}
        startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        disabled={loading || !formData.target_language || !formData.proficiency_level}
        sx={{
          py: { xs: 1, md: 1.5 },
          borderRadius: 2,
          fontSize: { xs: '0.9rem', sm: '1rem' },
          boxShadow: 2
        }}
      >
        {loading ? 'Finding ideas...' : 'Let\'s Go'}
      </Button>
    </Box>


    {/* Hidden proficiency level - keeping in UI as requested */}
    <FormControl fullWidth sx={{ mb: 3, display: 'none' }}>
      <InputLabel id="proficiency-label">Proficiency Level</InputLabel>
      <Select
        labelId="proficiency-label"
        id="proficiency_level"
        name="proficiency_level"
        value={formData.proficiency_level}
        onChange={handleInputChange}
        label="Proficiency Level"
        required
      >
        {proficiencyLevels.map(level => (
          <MenuItem key={level.value} value={level.value}>
            {level.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Button
      variant="outlined"
      onClick={() => setInterestsModalOpen(true)}
      fullWidth
      size="small"
      sx={{
        display: 'none',
        borderRadius: 2,
        py: { xs: 1, md: 1.25 },
        mt: 1
      }}
    >
      Choose Interests
    </Button>
  </Box>
</CardContent>

          </Card>
        </Grid>

        <Modal open={interestsModalOpen} onClose={() => setInterestsModalOpen(false)}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, maxWidth: 600, mx: 'auto', my: 5 }}>
            <Typography variant="h6" gutterBottom>Edit Interests</Typography>
            {renderUserInterests()}
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button onClick={() => setInterestsModalOpen(false)}>Close</Button>
            </Box>
          </Box>
        </Modal>

        {/* Content Ideas */}
        <Grid item xs={12} md={8} ref={storiesRef}>
          <Card>
            <CardHeader
              title="Choose Your Adventure"
              titleTypographyProps={{ variant: 'h6' }}
              subheader={
                formData.target_language ?
                `${getLanguageDisplayName(formData.target_language)} - ${getProficiencyDisplayName(formData.proficiency_level)}` :
                'Select language and level to generate ideas'
              }
            />
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : contentIdeas.length > 0 ? (
                <Grid container spacing={2}>
                  {contentIdeas.map((idea, index) => (
                    <Grid item xs={12} key={index}>
                      <Card
                        variant="outlined"
                        sx={{
                          bgcolor: selectedIdea === idea ? 'action.selected' : 'background.paper',
                          borderColor: selectedIdea === idea ? 'primary.main' : 'divider',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: 1
                          }
                        }}
                      >
                        <CardActionArea onClick={() => handleSelectIdea(idea)}>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={8}>
                                <Typography variant="h6" gutterBottom>
                                  {idea.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {idea.description}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <Chip
                                  icon={idea.type === 'conversation' ? <ChatIcon /> : <StoriesIcon />}
                                  label={idea.type === 'conversation' ? 'Conversation' : 'Story'}
                                  color="primary"
                                  variant="outlined"
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 1
                  }}
                >
                  <ExploreIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Ready to start your language adventure?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set your destination, choose your style, and let the story unfold.
                  </Typography>
                </Paper>
              )}
            </CardContent>
            {contentIdeas.length > 0 && (
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>

              </CardActions>
            )}
          </Card>
        </Grid>
        <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, maxWidth: 500, mx: 'auto', my: 10 }}>
            <Typography variant="h6" gutterBottom>Start Session?</Typography>
            <Typography variant="subtitle1" gutterBottom>{selectedIdea?.title}</Typography>
            <Typography variant="body2" gutterBottom>{selectedIdea?.description}</Typography>

            {selectedIdea?.roles?.a && selectedIdea?.roles?.b && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Which role do you want to play?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['a', 'b'].map((side) => {
                    const role = selectedIdea.roles[side];
                    const isSelected = selectedRole === side;
                    return (
                      <Button
                        key={side}
                        onClick={() => setSelectedRole(side)}
                        variant={isSelected ? 'contained' : 'outlined'}
                        sx={{ flex: '1 1 45%', textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role.role}
                          </Typography>
                          {role.goal && (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                              Goal: {role.goal}
                            </Typography>
                          )}
                        </Box>
                      </Button>
                    );
                  })}
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                  Your tutor will play the other role.
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setConfirmModalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => {
                  setConfirmModalOpen(false);
                  handleStartSession();
                }}
                startIcon={<ArrowForwardIcon />}
              >
                Start
              </Button>
            </Box>
          </Box>
        </Modal>
          </Grid>

      {/* Idea library browser */}
      <LibraryBrowser
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        kind="discover"
        onSelect={handleLibrarySelect}
        heading="Browse ideas"
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>

  );
};

export default ContentExplorerPage;